import React from "react";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import {
  ProfileInformationFields,
  ProfileInformationValues,
} from "@app.automotus.io/components/forms/ProfileInformationFields";
import { useMutation } from "@apollo/client";
import {
  UPDATE_PROFILE_ONBOARDING,
  UPDATE_PROFILE_ONBOARING_REFETCH_QUERIES,
  UpdateProfileOnboardingData,
  UpdateProfileOnboardingVars,
} from "common/graphql/mutations";
import { useIsMobile } from "@app.automotus.io/components/hooks";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLocation, useNavigate } from "react-router-dom";
import useUserProfile from "@app.automotus.io/components/hooks/useUserProfile";
import { FormObserver } from "@app.automotus.io/components/routes/signup/helpers/FormObserver";
import { profileInfoVar } from "@app.automotus.io/components/routes/signup/helpers/reactiveVar";
import { useLogout } from "@app.automotus.io/components/auth";

export const ProfileSetup: ProfileSetupComponent = ({ initialValues }) => {
  const navigate = useNavigate();
  const SPACING = 2;
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isSmall = useMediaQuery(theme.breakpoints.down("tiny"));

  const logout = useLogout();
  const { userProfile } = useUserProfile();
  const location = useLocation();

  const [updateBasicProfile, { error, called, reset, loading: updateBasicProfileLoading }] = useMutation<
    UpdateProfileOnboardingData,
    UpdateProfileOnboardingVars
  >(UPDATE_PROFILE_ONBOARDING, {
    refetchQueries: UPDATE_PROFILE_ONBOARING_REFETCH_QUERIES,
    onCompleted: () => {
      navigate("../vehicle", { state: location.state });
    },
  });

  return (
    <Box sx={{ mt: isMobile ? 1.5 : 3, px: isMobile ? 0 : 2 }}>
      <Typography variant={isDesktop ? "h4b" : isSmall ? "h4bxs" : "h4bsmall"}>
        <span style={{ color: theme.palette.primary.main }}>CurbPass</span> Profile
      </Typography>
      <Typography variant="body2" sx={{ mb: SPACING }}>
        We just need a few quick details.
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Formik<ProfileSetupFormValues>
          initialValues={{
            ...initialValues,
          }}
          validationSchema={ProfileSetup.validationSchema}
          enableReinitialize={true}
          onSubmit={async (values: ProfileSetupFormValues, actions) => {
            try {
              const {
                firstName: givenName,
                lastName: familyName,
                email,
                isAgreedToEmailUpdates: emailUpdates,
                phoneNumber,
                ownerType,
                organizationName,
              } = values;
              const selectedCompanies = "{other}";
              const variables: UpdateProfileOnboardingVars = {
                accountId: userProfile?.account.id || "",
                userId: userProfile?.id || "",
                fullName: `${givenName} ${familyName}`,
                givenName,
                familyName,
                email,
                phoneNumber,
                // Value is required, so will not be null
                selectedCompanies,
                emailUpdates,
                organizationName: ownerType === "fleet_operator" ? organizationName : "",
                ownerType,
              };

              profileInfoVar({ ...values });
              await updateBasicProfile({ variables });
            } catch (err) {
              // TODO: error analytics
              console.error(err);
            } finally {
              actions.setSubmitting(false);
              reset();
            }
          }}
        >
          {(props) => (
            <Form>
              <fieldset disabled={updateBasicProfileLoading} style={{ border: 0, padding: 0, margin: 0 }}>
                <FormObserver formType="profile" />
                <ProfileInformationFields />
                <Grid container direction="row" rowSpacing={2} sx={{ mt: 3 }}>
                  <Grid item xs={12}>
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      size={isSmall ? "small" : isMobile ? "medium" : "large"}
                      fullWidth
                      loading={props.isSubmitting}
                      sx={{
                        textTransform: "none",
                      }}
                    >
                      Save and Continue
                    </LoadingButton>
                  </Grid>
                  <Grid item xs={12}>
                    {called && error && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        An error occurred when saving your profile information. Please try again.
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </fieldset>
            </Form>
          )}
        </Formik>
      </Box>
      <Button
        variant={"text"}
        size={isSmall ? "small" : isMobile ? "medium" : "large"}
        fullWidth
        sx={{ textTransform: "none", p: 0 }}
        onClick={() => logout()}
      >
        Back
      </Button>
    </Box>
  );
};

ProfileSetup.defaultInitialValues = {
  ...ProfileInformationFields.defaultInitialValues,
  isAgreedToTermsOfService: false,
  isAgreedToEmailUpdates: true,
};

const validationSchema = ProfileInformationFields.validationSchema;

ProfileSetup.validationSchema = validationSchema;

export interface ProfileSetupProps {
  initialValues: ProfileSetupFormValues;
}

export type ProfileSetupFormValues = ProfileInformationValues & {
  isAgreedToEmailUpdates: boolean;
  isAgreedToTermsOfService: boolean;
};

export interface ProfileSetupComponent extends React.FC<ProfileSetupProps> {
  defaultInitialValues: ProfileSetupProps["initialValues"];
  validationSchema: typeof validationSchema;
}

export default ProfileSetup;
